import { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest } from '../services/apiClient';

const HEARTBEAT_INTERVAL = 20000; // 20 sn

/**
 * Müşteri hizmetleri personelinin durum kontrolü (Müsait/Meşgul/Görüşmede/Çevrimdışı).
 * - Heartbeat döngüsü ile çevrimiçi tutar.
 * - gorusmedeAktif true olduğunda otomatik "gorusmede"ye geçer, bitince önceki duruma döner.
 *
 * @param {object} opts
 * @param {boolean} opts.aktif        - Panel açık/softphone kayıtlı mı (heartbeat'i tetikler)
 * @param {boolean} opts.gorusmedeAktif - Aktif çağrı var mı (otomasyon)
 */
export function useSantralDurum({ aktif = false, gorusmedeAktif = false } = {}) {
  const [durum, setDurum] = useState('cevrimdisi');
  const [sebep, setSebep] = useState('');
  const [dahili, setDahili] = useState(null);
  const [atanmisDahiliVar, setAtanmisDahiliVar] = useState(true);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);

  const heartbeatRef = useRef(null);
  const mountedRef = useRef(true);
  const durumOncesiGorusmeRef = useRef('musait');
  const gorusmedeRef = useRef(false);
  const onlineRestoredRef = useRef(false);

  const benimDurumGetir = useCallback(async () => {
    try {
      const data = await apiRequest('/api/santral/personel/durum/benim', {
        method: 'GET',
        skipCsrf: true,
      });
      if (!mountedRef.current) return;
      const veri = data?.veri || {};
      setDurum(veri.durum || 'cevrimdisi');
      setSebep(veri.sebep || '');
      setDahili(veri.dahili || null);
      setAtanmisDahiliVar(veri.atanmisDahiliVar !== false);
    } catch (err) {
      if (!mountedRef.current) return;
      setHata(err?.message || 'Durum alınamadı');
    }
  }, []);

  const durumDegistir = useCallback(async (yeniDurum, yeniSebep = '') => {
    setYukleniyor(true);
    setHata(null);
    try {
      const data = await apiRequest('/api/santral/personel/durum', {
        method: 'POST',
        body: { durum: yeniDurum, sebep: yeniSebep },
      });
      if (!mountedRef.current) return;
      const veri = data?.veri || {};
      setDurum(veri.durum || yeniDurum);
      setSebep(veri.sebep || yeniSebep);
    } catch (err) {
      if (!mountedRef.current) return;
      setHata(err?.message || 'Durum güncellenemedi');
    } finally {
      if (mountedRef.current) setYukleniyor(false);
    }
  }, []);

  const heartbeatGonder = useCallback(async () => {
    try {
      const data = await apiRequest('/api/santral/personel/heartbeat', {
        method: 'POST',
        skipCsrf: true,
      });
      if (!mountedRef.current) return;
      const veri = data?.veri || {};
      if (veri.durum) setDurum((onceki) => (onceki !== veri.durum ? veri.durum : onceki));
    } catch {
      // Heartbeat hataları sessiz
    }
  }, []);

  // Sayfa açılışında kayıtlı durumu yükle (mola vb. yenilemeden önce görünsün)
  useEffect(() => {
    benimDurumGetir();
  }, [benimDurumGetir]);

  // Softphone kayıt olduğunda: kayıtlı durumu koru, yalnızca çevrimdışıysa müsait yap
  useEffect(() => {
    mountedRef.current = true;
    if (!aktif) {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      onlineRestoredRef.current = false;
      return undefined;
    }

    if (onlineRestoredRef.current) {
      return undefined;
    }

    let iptal = false;

    const baglantiyiBaslat = async () => {
      try {
        const data = await apiRequest('/api/santral/personel/durum/benim', {
          method: 'GET',
          skipCsrf: true,
        });
        if (iptal || !mountedRef.current) return;

        const veri = data?.veri || {};
        const mevcutDurum = veri.durum || 'cevrimdisi';
        const mevcutSebep = veri.sebep || '';

        setDurum(mevcutDurum);
        setSebep(mevcutSebep);
        setDahili(veri.dahili || null);
        setAtanmisDahiliVar(veri.atanmisDahiliVar !== false);

        if (mevcutDurum === 'cevrimdisi') {
          await durumDegistir('musait');
        } else if (mevcutDurum === 'gorusmede') {
          // Aktif çağrı yoksa eski "görüşmede" kaydını müsait'e çek
          await durumDegistir('musait');
        } else {
          // mesgul / musait: DB + Netgsm agentpause senkronu
          await durumDegistir(mevcutDurum, mevcutSebep);
        }
      } catch {
        if (!iptal && mountedRef.current) {
          await durumDegistir('musait');
        }
      }

      if (iptal || !mountedRef.current) return;

      onlineRestoredRef.current = true;
      heartbeatGonder();
      heartbeatRef.current = setInterval(heartbeatGonder, HEARTBEAT_INTERVAL);
    };

    baglantiyiBaslat();

    return () => {
      iptal = true;
    };
  }, [aktif, durumDegistir, heartbeatGonder]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Görüşmede otomasyonu
  useEffect(() => {
    if (!aktif) return;

    if (gorusmedeAktif && !gorusmedeRef.current) {
      gorusmedeRef.current = true;
      // Önceki durumu hatırla (gorusmede dışı), çağrı bitince geri dön
      durumOncesiGorusmeRef.current = durum === 'gorusmede' ? 'musait' : durum;
      if (durum !== 'gorusmede') {
        durumDegistir('gorusmede');
      }
    } else if (!gorusmedeAktif && gorusmedeRef.current) {
      gorusmedeRef.current = false;
      const geriDon = durumOncesiGorusmeRef.current === 'gorusmede'
        ? 'musait'
        : durumOncesiGorusmeRef.current || 'musait';
      durumDegistir(geriDon);
    }
  }, [aktif, gorusmedeAktif, durum, durumDegistir]);

  return {
    durum,
    sebep,
    dahili,
    atanmisDahiliVar,
    yukleniyor,
    hata,
    durumDegistir,
    yenile: benimDurumGetir,
  };
}

export default useSantralDurum;
